import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BackLink, PageHeader } from '@/layouts/AppLayout';

const schema = z
  .object({
    hotelId: z.string().uuid('Select a hotel'),
    clientId: z.string().uuid('Select a client'),
    hotelRoomTypeId: z.string().uuid('Select a room type'),
    mealPlan: z.string().min(1, 'Meal plan is required').max(20),
    occupancy: z.enum(['single', 'double', 'triple', 'child']),
    rate: z.coerce.number().positive('Rate must be positive'),
    currency: z.string().max(3).optional(),
    validFrom: z.string().min(1, 'Valid from is required'),
    validTo: z.string().min(1, 'Valid to is required'),
    notes: z.string().optional(),
  })
  .refine((d) => new Date(d.validTo) >= new Date(d.validFrom), {
    message: 'Valid to must be on or after valid from',
    path: ['validTo'],
  });

type FormValues = z.infer<typeof schema>;

interface SelectOption { id: string; name: string }
interface RoomType { id: string; name: string; code: string | null }

const MEAL_PLANS = ['CP', 'MAP', 'AP', 'APAI', 'MAPAI', 'CPI', 'EP'];

export function CreateRatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  const { data: hotels = [] } = useQuery({
    queryKey: ['hotels-list'],
    queryFn: () => api.get<SelectOption[]>('/hotels'),
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn: () => api.get<SelectOption[]>('/clients'),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      hotelId: searchParams.get('hotelId') ?? '',
      clientId: searchParams.get('clientId') ?? '',
      occupancy: 'double',
      currency: 'INR',
    },
  });

  const hotelId = watch('hotelId');
  const clientId = watch('clientId');
  const hotelRoomTypeId = watch('hotelRoomTypeId');
  const occupancy = watch('occupancy');
  const mealPlan = watch('mealPlan');

  const { data: roomTypes = [] } = useQuery({
    queryKey: ['hotels', hotelId, 'room-types'],
    queryFn: () => api.get<RoomType[]>(`/hotels/${hotelId}/room-types`),
    enabled: !!hotelId,
  });

  const mutation = useMutation({
    mutationFn: (data: FormValues) =>
      api.post('/contracted-rates', { ...data, rate: String(data.rate) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracted-rates'] });
      toast.success('Rate created');
      navigate('/rates');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to create rate';
      setError('root', { message: msg });
    },
  });

  return (
    <div>
      <PageHeader title="Add contracted rate" />
      <div className="p-6 max-w-2xl">
        <BackLink to="/rates" label="Back to rates" />
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Hotel & client</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Hotel *</Label>
                <Select
                  value={hotelId}
                  onValueChange={(v) => {
                    setValue('hotelId', v, { shouldValidate: true });
                    setValue('hotelRoomTypeId', '', { shouldValidate: false });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select hotel…" />
                  </SelectTrigger>
                  <SelectContent>
                    {hotels.map((h) => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.hotelId && <p className="text-xs text-danger-700">{errors.hotelId.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Client *</Label>
                <Select
                  value={clientId}
                  onValueChange={(v) => setValue('clientId', v, { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.clientId && <p className="text-xs text-danger-700">{errors.clientId.message}</p>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Rate details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Room type *</Label>
                  <Select
                    value={hotelRoomTypeId ?? ''}
                    onValueChange={(v) => setValue('hotelRoomTypeId', v, { shouldValidate: true })}
                    disabled={!hotelId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={hotelId ? 'Select room type…' : 'Select hotel first'} />
                    </SelectTrigger>
                    <SelectContent>
                      {roomTypes.map((rt) => (
                        <SelectItem key={rt.id} value={rt.id}>
                          {rt.name}{rt.code ? ` (${rt.code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.hotelRoomTypeId && (
                    <p className="text-xs text-danger-700">{errors.hotelRoomTypeId.message}</p>
                  )}
                  {hotelId && roomTypes.length === 0 && (
                    <p className="text-xs text-neutral-400">
                      No room types defined.{' '}
                      <a href={`/hotels/${hotelId}`} className="text-tangerine-600 hover:underline">
                        Add them on the hotel page.
                      </a>
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Meal plan *</Label>
                  <Select
                    value={mealPlan}
                    onValueChange={(v) => setValue('mealPlan', v, { shouldValidate: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEAL_PLANS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.mealPlan && <p className="text-xs text-danger-700">{errors.mealPlan.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Occupancy</Label>
                  <Select
                    value={occupancy}
                    onValueChange={(v) => setValue('occupancy', v as FormValues['occupancy'], { shouldDirty: true })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="double">Double</SelectItem>
                      <SelectItem value="triple">Triple</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rate">Rate (₹) *</Label>
                  <Input id="rate" type="number" min={0} step="0.01" {...register('rate')} />
                  {errors.rate && <p className="text-xs text-danger-700">{errors.rate.message}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="validFrom">Valid from *</Label>
                  <Input id="validFrom" type="date" {...register('validFrom')} />
                  {errors.validFrom && <p className="text-xs text-danger-700">{errors.validFrom.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="validTo">Valid to *</Label>
                  <Input id="validTo" type="date" {...register('validTo')} />
                  {errors.validTo && <p className="text-xs text-danger-700">{errors.validTo.message}</p>}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" {...register('notes')} placeholder="Any remarks…" />
              </div>
            </CardContent>
          </Card>

          {errors.root && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{errors.root.message}</p>
          )}
          <div className="flex gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create rate'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/rates')}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
